/**
 * Invoicing Tracker API client. Thin wrappers over the Firebase-authed
 * apiRequest base, hitting /api/admin/invoicing.
 */

import { apiRequest, API_BASE_URL, getAuthToken } from './api';
import {
  Invoice,
  InvoicePatch,
  InvoicingResponse,
  InvoicingStatus,
  ImportSummary,
  PacingSyncSummary,
  GamAutoLinkSummary,
  IoBackfillSummary,
  BillingContactsImportSummary,
  GeneratedInvoice,
  DedupeResult,
} from '../components/Invoicing/types';

const BASE = '/api/admin/invoicing';

export async function getInvoicingStatus(): Promise<InvoicingStatus> {
  return apiRequest(`${BASE}/status`);
}

export async function getInvoices(opts?: {
  month?: string;
  needsReview?: boolean;
}): Promise<InvoicingResponse> {
  const params = new URLSearchParams();
  if (opts?.month) params.set('month', opts.month);
  if (opts?.needsReview) params.set('needsReview', 'true');
  const qs = params.toString();
  return apiRequest(`${BASE}/invoices${qs ? `?${qs}` : ''}`);
}

export async function getInvoice(id: string): Promise<{ invoice: Invoice }> {
  return apiRequest(`${BASE}/invoices/${encodeURIComponent(id)}`);
}

export async function createInvoice(body: Partial<Invoice>): Promise<{ invoice: Invoice }> {
  return apiRequest(`${BASE}/invoices`, { method: 'POST', body: JSON.stringify(body) });
}

export async function updateInvoice(id: string, patch: InvoicePatch): Promise<{ invoice: Invoice }> {
  return apiRequest(`${BASE}/invoices/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

export async function markInvoiceReviewed(id: string): Promise<{ invoice: Invoice }> {
  return apiRequest(`${BASE}/invoices/${encodeURIComponent(id)}/review`, { method: 'POST' });
}

export async function importSheet(body?: {
  spreadsheetId?: string;
  tabs?: string[];
  dryRun?: boolean;
  purge?: boolean;
}): Promise<{ summary: ImportSummary }> {
  return apiRequest(`${BASE}/import-sheet`, {
    method: 'POST',
    body: JSON.stringify(body ?? {}),
  });
}

export async function resyncPacing(body?: {
  month?: string;
  invoiceId?: string;
}): Promise<{ summary: PacingSyncSummary }> {
  return apiRequest(`${BASE}/resync-pacing`, {
    method: 'POST',
    body: JSON.stringify(body ?? {}),
  });
}

export async function autoLinkGam(body?: {
  month?: string;
  invoiceId?: string;
  apply?: boolean;
  thenSyncPacing?: boolean;
}): Promise<{ summary: GamAutoLinkSummary }> {
  return apiRequest(`${BASE}/auto-link-gam`, {
    method: 'POST',
    body: JSON.stringify(body ?? {}),
  });
}

export interface AcceptGamLinkResult {
  applied: boolean;
  reason?: string;
  gamLineItemIds?: string[];
  monthKey?: string;
}

/**
 * Confirm a fuzzy GAM link suggestion (one-click "Link"). Sends only the ORDER —
 * the backend re-fetches its current linkable line items so a stale suggestion
 * can never link outdated ids. 409 when the order has no linkable line items or
 * a manual link exists.
 */
export async function acceptGamLink(
  invoiceId: string,
  body: { gamOrderId: string; gamOrderName?: string; matchMethod?: 'order-number' | 'advertiser-date' },
): Promise<{ result: AcceptGamLinkResult }> {
  return apiRequest(`${BASE}/invoices/${encodeURIComponent(invoiceId)}/accept-gam-link`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function backfillIo(body?: {
  month?: string;
  invoiceId?: string;
  force?: boolean;
}): Promise<{ summary: IoBackfillSummary }> {
  return apiRequest(`${BASE}/backfill-io`, {
    method: 'POST',
    body: JSON.stringify(body ?? {}),
  });
}

export async function importBillingContacts(body?: {
  spreadsheetId?: string;
  tab?: string;
}): Promise<{ summary: BillingContactsImportSummary }> {
  return apiRequest(`${BASE}/import-billing-contacts`, {
    method: 'POST',
    body: JSON.stringify(body ?? {}),
  });
}

/**
 * Find (apply=false, dry-run preview) or merge (apply=true) duplicate invoice
 * docs for a month. Preview needs invoicing.manage; apply needs invoicing.billing.
 */
export async function dedupeInvoices(body: {
  month: string;
  apply?: boolean;
}): Promise<{ result: DedupeResult }> {
  return apiRequest(`${BASE}/dedupe`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** Generate (or regenerate) ONE month's invoice PDF, keyed by the invoice id. */
export async function generateMonthlyInvoice(invoiceId: string): Promise<{ generated: GeneratedInvoice }> {
  return apiRequest(`${BASE}/invoices/${encodeURIComponent(invoiceId)}/generate-invoice`, {
    method: 'POST',
  });
}

/**
 * Refresh this month's true delivered impressions (date-ranged GAM report).
 * Returns immediately (202); the report runs in the background and writes the
 * figure to the invoice — poll the invoices list for `monthDeliveredAsOf`.
 */
export async function refreshMonthlyDelivered(invoiceId: string): Promise<{ status: string }> {
  return apiRequest(`${BASE}/invoices/${encodeURIComponent(invoiceId)}/refresh-monthly-delivered`, {
    method: 'POST',
  });
}

/**
 * Download a month's generated invoice PDF through the authenticated proxy (no
 * dependency on Drive link-sharing) and trigger a browser download.
 */
export async function downloadMonthlyInvoice(invoiceId: string, fileName?: string): Promise<void> {
  const token = await getAuthToken();
  const res = await fetch(
    `${API_BASE_URL}${BASE}/invoices/${encodeURIComponent(invoiceId)}/generated/file`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: 'Download failed' }));
    throw new Error(body.error || `Download failed (${res.status})`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName || `${invoiceId}-invoice.pdf`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
